/**
 * Builds the SRP view model from +data payload (or StaticSRPData) for SSR and client.
 * Used to populate #srp-initial script and to drive the first paint of the island.
 */
import type { StaticSRPData } from '../../types/srp-static';
import type { SrpViewModel, SrpQueryState, SrpFacetsData, SrpFacetItem, SrpPaginationViewModel, LastSeenOptionValue, MarketHeaderStaticData } from './SrpViewModel';
import { getTranslatedLocationForHeader } from '../../utils/location/translate-location-for-header';
import { generateLocationMarketHeaderText } from '../../utils/location/generate-location-market-header-text';
import type { LocationHeaderMetrics } from '../../utils/location/generate-location-market-header-text';
import type { LocationTrendsData } from '../../hooks/useLocationTrends';
import { buildHitCompactViewPropsFromStatic } from '../../pages/index/static/buildHitCompactViewProps';
import { hitPropsToSrpJobCard } from './hitPropsToSrpJobCard';
import { generateUrlFromIndexUiState } from '../../utils/algolia/url-handling';
import type { Locale } from '../../utils/routing/generate-url';
import type { FacetValue } from '../../utils/facets/facets-shared';
import { generateStatsText, showFinlandSubtitle as computeShowFinlandSubtitle } from '../../utils/stats/generateStatsText';
import type { StatsLocale } from '../../utils/stats/generateStatsText';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { buildSrpStaticStrings } from './buildSrpStaticStrings';
import { buildFooterStaticStrings } from '../../utils/footer/buildFooterStaticStrings';
import { isToolsPage } from '../../utils/routing/route-utils';

export interface BuildSrpViewModelInput {
  /** Same shape as +data return for SRP (or StaticSRPData). */
  data: StaticSRPData;
  locale: Locale;
  pathname: string;
  /** Optional page title (defaults to a generic search title). */
  title?: string;
  /** Optional canonical URL (defaults to pathname + search). */
  canonicalUrl?: string;
}

function getFiltersObj(data: StaticSRPData): Record<string, string[]> {
  const f = data.initialFilters;
  if (typeof f === 'string') {
    try {
      return (JSON.parse(f) as Record<string, string[]>) || {};
    } catch {
      return {};
    }
  }
  return (f as Record<string, string[]>) || {};
}

const LAST_SEEN_OPTIONS: { value: LastSeenOptionValue; hours: number }[] = [
  { value: 'all', hours: 0 },
  { value: '6h', hours: 6 },
  { value: '24h', hours: 24 },
  { value: '3d', hours: 72 },
  { value: '1w', hours: 168 },
  { value: '2w', hours: 336 },
  { value: '1m', hours: 720 },
];

function lastSeenMinToOption(timestampStr: string | undefined): LastSeenOptionValue {
  if (!timestampStr || timestampStr === 'all') return 'all';
  const num = Number(timestampStr);
  if (!Number.isFinite(num)) return 'all';
  const now = Date.now();
  let best: LastSeenOptionValue = 'all';
  let smallestDiff = Infinity;
  for (const option of LAST_SEEN_OPTIONS) {
    if (option.hours > 0) {
      const expectedThreshold = now - option.hours * 60 * 60 * 1000;
      const diff = Math.abs(num - expectedThreshold);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        best = option.value;
      }
    }
  }
  return best;
}

/**
 * Builds the full SrpViewModel from page data.
 * Call on the server when rendering the SRP route (and optionally on the client when parsing #srp-initial).
 */
export function buildSrpViewModel(input: BuildSrpViewModelInput): SrpViewModel {
  const { data, locale, pathname, title, canonicalUrl } = input;
  const filtersObj = getFiltersObj(data);
  const results = data.initialResults;
  const hits = results?.hits ?? [];
  const nbHits = results?.nbHits ?? 0;
  const page = data.initialPage ?? 0;
  const nbPages = results?.nbPages ?? 1;
  const indexName = data.indexName ?? `${locale}_search`;
  const queryState: SrpQueryState = {
    query: data.initialQuery ?? '',
    refinementList: filtersObj,
    page,
    sort: data.initialSort,
  };

  const jobs = hits.map((hit: Record<string, unknown>, index: number) => {
    const props = buildHitCompactViewPropsFromStatic(hit, index, data, locale, pathname);
    return hitPropsToSrpJobCard(props);
  });

  const facets: SrpFacetsData = {};
  const rawFacets = data.facetsData ?? {};
  Object.entries(rawFacets).forEach(([attribute, items]) => {
    const list = (items as FacetValue[]).map((item) => ({
      value: item.value,
      label: item.label ?? item.value,
      count: item.count,
      selected: 'isRefined' in item ? item.isRefined : (filtersObj[attribute] ?? []).includes(item.value),
    }));
    facets[attribute] = list as SrpFacetItem[];
  });

  const prevUrl =
    page > 0
      ? generateUrlFromIndexUiState(
          { query: queryState.query, refinementList: filtersObj, page: page - 1 },
          indexName,
          pathname
        )
      : null;
  const nextUrl =
    page < nbPages - 1
      ? generateUrlFromIndexUiState(
          { query: queryState.query, refinementList: filtersObj, page: page + 1 },
          indexName,
          pathname
        )
      : null;

  const pagination: SrpPaginationViewModel = {
    currentPage: page,
    nbPages,
    nbHits,
    prevUrl,
    nextUrl,
  };

  const canonical = canonicalUrl ?? pathname;

  const locationFilters = filtersObj.location;
  const location: string | null =
    Array.isArray(locationFilters) && locationFilters.length === 1 ? locationFilters[0] : null;
  const companyId: string | null =
    Array.isArray(filtersObj.companyId) && filtersObj.companyId.length > 0
      ? filtersObj.companyId[0]
      : null;

  // From cookies at render time (initialLayout) so static shell renders correct view and hit layout
  const viewMode = (data.initialLayout as 'grid' | 'list' | 'map') || 'list';
  const currentRefinement = indexName;
  const hasActiveSearch =
    (queryState.query?.trim() ?? '').length > 0 ||
    Object.entries(filtersObj).some(([, v]) =>
      Array.isArray(v) ? v.some(Boolean) : v != null && String(v).length > 0
    );

  const statsText = generateStatsText({
    nbHits,
    filters: filtersObj,
    query: queryState.query ?? '',
    locale: locale as StatsLocale,
  });
  const showFinlandSubtitle = computeShowFinlandSubtitle(
    statsText,
    filtersObj,
    queryState.query ?? ''
  );

  const messages = loadLocaleData(locale);
  const staticStrings = buildSrpStaticStrings({
    messages,
    locale,
    nbHits,
  });
  const footerStrings = buildFooterStaticStrings(messages);

  let marketHeaderStatic: MarketHeaderStaticData | undefined;
  if (location) {
    const translatedLocation = getTranslatedLocationForHeader(location, locale as 'en' | 'fi' | 'sv');
    const title = (staticStrings.marketHeader?.title ?? 'Open jobs in {location}').replace('{location}', translatedLocation);
    const trends = data.locationTrendsData as LocationTrendsData | undefined;
    const salaryStats = trends?.salaryStats?.aggregate;
    const remotenessDistribution = trends?.trends?.remotenessDistribution ?? {};
    const languageRequirements = trends?.trends?.languageRequirements ?? {};
    const onsite = remotenessDistribution.onsite ?? 0;
    const hybrid = remotenessDistribution.hybrid ?? 0;
    const remote = remotenessDistribution.remote ?? 0;
    const total = onsite + hybrid + remote;
    const remotePercentage = total > 0 ? Math.round(((remote + hybrid) / total) * 100) : 0;
    const english = languageRequirements['English'] ?? 0;
    const finnish = languageRequirements['Finnish'] ?? languageRequirements['Suomi'] ?? 0;
    const swedish = languageRequirements['Swedish'] ?? languageRequirements['Svenska'] ?? 0;
    const local = finnish + swedish;
    const languageTotal = english + local;
      const companySum = trends?.trends?.companyDistribution
        ? Object.values(trends.trends.companyDistribution).reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0)
        : 0;
      const sampleCount = salaryStats?.sampleSize ?? (companySum > 0 ? companySum : nbHits);
    const avgSalaryNum = salaryStats?.average?.min ?? salaryStats?.median?.min ?? 0;
    const avgSalaryFormatted = avgSalaryNum > 0 ? `€${Math.round(avgSalaryNum / 1000)}k` : 'N/A';
    const companyEntries = trends?.trends?.companyDistribution ? Object.entries(trends.trends.companyDistribution) : [];
    const topCompanies = companyEntries.sort(([, a], [, b]) => (b as number) - (a as number)).slice(0, 3).map(([name]) => name);
    const fieldEntries = trends?.trends?.fieldDistribution ? Object.entries(trends.trends.fieldDistribution) : [];
    const topIndustries = fieldEntries.sort(([, a], [, b]) => (b as number) - (a as number)).slice(0, 3).map(([name]) => name);
    const metrics: LocationHeaderMetrics = {
      location: translatedLocation,
      locale: locale as 'en' | 'fi' | 'sv',
      nbHits,
      sampleCount,
      remotePercentage,
      avgSalaryNum,
      avgSalaryFormatted,
      languageTotals: { english, local, total: languageTotal },
      topCompanies: topCompanies ?? [],
      topIndustries: topIndustries ?? [],
      finlandAverages: trends?.finlandAverages,
    };
    const locationHeaderText = generateLocationMarketHeaderText(metrics);
    const { location: _loc, ...restFilters } = filtersObj;
    const removeLocationUrl = generateUrlFromIndexUiState(
      { query: queryState.query ?? '', refinementList: restFilters, page: 0, sort: queryState.sort },
      indexName,
      pathname
    );
    marketHeaderStatic = {
      translatedLocation,
      title,
      overviewText: locationHeaderText.overviewText,
      salaryBlurb: locationHeaderText.salaryBlurb,
      workModelBlurb: locationHeaderText.workModelBlurb,
      languageBlurb: locationHeaderText.languageBlurb,
      removeLocationUrl,
    };
  }

  return {
    locale,
    title: title ?? 'Job Search',
    canonicalUrl: canonical,
    query: queryState,
    jobs,
    facets,
    pagination,
    pathname,
    indexName,
    hasNoResults: (data.hasNoResults ?? false) || nbHits === 0,
    location,
    companyId,
    locationTrendsData: data.locationTrendsData,
    marketHeaderStatic,
    viewMode,
    currentRefinement,
    hasActiveSearch,
    statsText,
    showFinlandSubtitle,
    staticStrings,
    footerStrings,
    initialResults: results ?? null,
    initialSavedJobIds: data.initialSavedJobIds ?? [],
    initialAppliedJobIds: data.initialAppliedJobIds ?? [],
    initialViewedJobIds: data.initialViewedJobIds ?? [],
    initialFacetsData: data.facetsData ?? undefined,
    initialApplicationStateByJobId: data.initialApplicationStateByJobId ?? undefined,
    userAvatarUrl: data.userAvatarUrl ?? null,
    userName: data.userName ?? null,
    initialLastSeenOption: lastSeenMinToOption(filtersObj.lastSeenMin?.[0]),
    isToolsPage: isToolsPage(pathname),
  };
}

/**
 * Returns the URL for a given page index when rendering static pagination.
 * Used by SearchPagination in static mode.
 */
export function getPageUrlFromSrpViewModel(model: SrpViewModel, page: number): string {
  return generateUrlFromIndexUiState(
    {
      query: model.query.query,
      refinementList: model.query.refinementList,
      page,
      sort: model.query.sort,
    },
    model.indexName,
    model.pathname
  );
}
